<template>
  <!-- Edit the system's classification (C, I, A) or what it can recover to (RTO, RPO), in a side panel (US 9370) -->
  <V2SidePanel ref="panel"
               :kind="$t('system_hero.panel.kind')"
               :title="title"
               :intro="intro"
               :busy="saving"
               qid="system-classification-panel">
    <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>

    <template v-if="mode === 'classification'">
      <div v-for="key in ['confidentiality', 'integrity', 'availability']" :key="key" class="v2-field sx-class">
        <span class="v2-field__label">{{ $t('systems.assessments.' + key + '_title') }}</span>
        <div class="sx-class__row">
          <span class="sx-class__lab">{{ $t('assessment_view.impact') }}</span>
          <div class="sx-toggles">
            <button v-for="(level, index) in impactLevels"
                    :key="level"
                    type="button"
                    :class="{ on: form[key + '_impact'] === level }"
                    :aria-pressed="form[key + '_impact'] === level ? 'true' : 'false'"
                    :qid="'system-class-' + key + '-impact-' + (index + 1)"
                    @click="pick(key + '_impact', level)">{{ index + 1 }} {{ $t('impact.' + impactKey(level)) }}</button>
          </div>
        </div>
        <div class="sx-class__row">
          <span class="sx-class__lab">{{ $t('assessment_view.probability') }}</span>
          <div class="sx-toggles">
            <button v-for="(level, index) in probabilityLevels"
                    :key="level"
                    type="button"
                    :class="{ on: form[key + '_probability'] === level }"
                    :aria-pressed="form[key + '_probability'] === level ? 'true' : 'false'"
                    @click="pick(key + '_probability', level)">{{ index + 1 }} {{ $t('probability.' + impactKey(level)) }}</button>
          </div>
        </div>
        <b-form-textarea v-model="form[key + '_description']" rows="2" max-rows="6" :placeholder="$t('systems.assessments.description_placeholder')"></b-form-textarea>
      </div>
    </template>

    <template v-else>
      <div class="v2-field">
        <span class="v2-field__label">{{ $t('assessment_view.capability') }}</span>
        <div class="v2-field__two">
          <b-form-input v-model="form.number" type="number" min="0" :placeholder="$t('systems.assessments.rpo_number_placeholder')" :qid="'system-' + mode + '-number'"></b-form-input>
          <b-form-select v-model="form.type" :options="timeOptions" :qid="'system-' + mode + '-type'"></b-form-select>
        </div>
        <small class="v2-field__note">{{ $t('systems.assessments.' + mode + '_description') }}</small>
      </div>
      <div class="v2-field">
        <span class="v2-field__label">{{ $t('systems.assessments.description_label') }}</span>
        <b-form-textarea v-model="form.description" rows="3" max-rows="8" :placeholder="$t('systems.assessments.description_placeholder')"></b-form-textarea>
      </div>
    </template>

    <template #foot>
      <span class="v2-panel__grow"></span>
      <b-button variant="secondary" @click="$refs.panel.hide()">{{ $t('systems.create.cancel') }}</b-button>
      <b-button variant="success" :disabled="saving" qid="system-classification-submit" @click="save">
        <font-awesome-icon :icon="saving ? 'spinner' : 'check'" :class="{ 'fa-spin': saving }"/> {{ $t('system_hero.panel.save') }}
      </b-button>
    </template>
  </V2SidePanel>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import V2SidePanel from '@/components/V2SidePanel.vue';
  import {IMPACT_LEVELS, impactKey} from '@/util/impactLevels.js';

  const PROBABILITY_LEVELS = ['Very unlikely', 'Unlikely', 'Likely', 'Very likely'];
  const TIME_UNITS = ['Seconds', 'Minutes', 'Hours', 'Days', 'Months', 'Years'];

  export default {
    name: 'SystemClassificationPanel',
    components: {
      V2SidePanel
    },
    props: {
      system: { type: Object, required: true }
    },
    data: function() {
      return {
        mode: 'classification',
        form: {},
        saving: false,
        error: '',
        impactLevels: IMPACT_LEVELS,
        probabilityLevels: PROBABILITY_LEVELS
      }
    },
    computed: {
      title() {
        return this.mode === 'classification' ? this.$t('systems.assessments.system_classification_title') : this.$t('systems.assessments.' + this.mode + '_title');
      },
      intro() {
        return this.mode === 'classification' ? this.$t('systems.assessments.system_classification_description') : this.$t('assessment_view.capability_intro', { name: this.system.name });
      },
      timeOptions() {
        let units = { Seconds: 'seconds', Minutes: 'minutes', Hours: 'hours', Days: 'days', Months: 'months', Years: 'years' };
        return [{ value: null, text: this.$t('systems.assessments.rpo_type_placeholder') }]
          .concat(TIME_UNITS.map(unit => ({ value: unit, text: this.$t('system.interval_' + units[unit]) })));
      }
    },
    methods: {
      impactKey: impactKey,
      // mode: classification, rto or rpo
      show(mode) {
        let system = this.system;
        this.mode = mode;
        this.error = '';
        if (mode === 'classification') {
          let form = {};
          ['confidentiality', 'integrity', 'availability'].forEach(function(key) {
            form[key + '_impact'] = system[key + '_impact'] || null;
            form[key + '_probability'] = system[key + '_probability'] || null;
            form[key + '_description'] = system[key + '_description'] || '';
          });
          this.form = form;
        } else {
          this.form = {
            number: system[mode + '_number'] || '',
            type: system[mode + '_type'] || null,
            description: system[mode + '_description'] || ''
          };
        }
        this.$refs.panel.show();
      },
      pick(field, level) {
        this.form[field] = this.form[field] === level ? null : level;
      },
      save() {
        let self = this;
        let data = this.mode === 'classification' ? this.form : {
          [this.mode + '_number']: this.form.number,
          [this.mode + '_type']: this.form.type,
          [this.mode + '_description']: this.form.description
        };
        this.saving = true;
        this.error = '';
        piincHttp.put('systems/' + this.system.slug, data).then(function(response) {
          self.saving = false;
          self.$refs.panel.hide();
          self.$toastr('success', self.mode === 'classification' ? self.$t('systems.assessments.system_classification_success') : self.$t('systems.assessments.' + self.mode + '_success'));
          self.$emit('saved', response.data);
        }, function(error) {
          self.saving = false;
          self.error = (error.data && error.data.message) || self.$t('system_hero.panel.error');
        });
      }
    }
  }
</script>
